import * as React from "react";
import { isAxiosError } from "axios";
import { useCreateDocument } from "@/hooks/useCreateDocument";
import { useDocument } from "@/hooks/useDocument";
import { useUser } from "@/hooks/useUser";
import { CreateDocumentPayload } from "@/types/document.types";

export const useDocumentCreationOnError = () => {
  const { user } = useUser();
  const { data: document, error: documentError } = useDocument();
  const { mutate: createDocument, isPending: isCreating } = useCreateDocument();

  // Track if we've already attempted to create a document for this 404 error
  const documentCreationAttemptedRef = React.useRef<boolean>(false);

  // Handle 404 error and create document
  React.useEffect(() => {
    // Reset the flag when the document is successfully loaded or when there's no error
    if (document || !documentError) {
      documentCreationAttemptedRef.current = false;
    }

    if (
      documentError &&
      isAxiosError(documentError) &&
      documentError?.response?.status === 404 &&
      user &&
      !isCreating &&
      !documentCreationAttemptedRef.current // Only create if we haven't already attempted
    ) {
      // Mark that we're attempting to create a document immediately
      documentCreationAttemptedRef.current = true;

      const createNewDocument = async () => {
        try {
          const { getDocumentInfo } = await import("@/utils/document.utils");
          const { getDocumentText } = await import("@/taskpane/taskpane");

          const [docInfo, content] = await Promise.all([
            getDocumentInfo(),
            getDocumentText()
          ]);

          const createPayload: CreateDocumentPayload = {
            editor_id: user.id,
            title: docInfo.name,
            content: content || "", // Use fetched content
            collaborators: [user.id],
            doc_type: "ms_word",
            ms_word_id: docInfo.id,
          };

          createDocument(createPayload, {
            onError: () => {
              // Reset the flag on error so user can retry if needed
              documentCreationAttemptedRef.current = false;
            },
            // onSuccess is handled by the mutation hook which invalidates queries
          });
        } catch (error) {
          console.error("Failed to create document:", error);
          // Reset the flag on error so user can retry if needed
          documentCreationAttemptedRef.current = false;
        }
      };

      createNewDocument();
    }
  }, [documentError, user, createDocument, isCreating, document]);
};
